import React, { useEffect, useState } from "react";
import {
  Drawer,
  List,
  Typography,
  Button,
  Empty,
  Spin,
  Tooltip,
  Badge,
  notification as antNotification,
  Space,
  Dropdown,
} from "antd";
import {
  BellOutlined,
  DeleteOutlined,
  CheckOutlined,
  MoreOutlined,
  CheckSquareOutlined,
} from "@ant-design/icons";
import { NotificationService } from "../../services/NotificationService";
import {
  Notification,
  NotificationStatus,
  NotificationType,
} from "../../model_gen/notification";
import { usePermissions } from "../../PermissionsProvider";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { CompanyService } from "../../services/CompanyService";
import { Company } from "../../model_gen/company";
import { useWebSocket } from "../../utils/Websockets";

interface AlertTrayProps {
  visible: boolean;
  onClose: () => void;
  refreshTrigger?: number;
  onNotificationsRead?: () => void;
}

const AlertTray: React.FC<AlertTrayProps> = ({
  visible,
  onClose,
  refreshTrigger = 0,
  onNotificationsRead,
}) => {
  const { apiKey } = usePermissions();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [markingAllRead, setMarkingAllRead] = useState<boolean>(false);
  const [company, setCompany] = useState<Company | null>(null);
  const [archivingAll, setArchivingAll] = useState<boolean>(false);
  const [hoveredNotificationId, setHoveredNotificationId] = useState<
    number | null
  >(null);

  // Fetch company for websocket key
  useEffect(() => {
    if (apiKey) {
      CompanyService.getCompany(null, apiKey).subscribe(
        (result: Company) => {
          setCompany(result);
        },
        (error: any) => {
          console.error(error);
        }
      );
    }
  }, [apiKey]);

  // Fetch notifications when drawer is opened or refreshTrigger changes
  useEffect(() => {
    if (visible) {
      fetchNotifications();
    }
  }, [visible, refreshTrigger]);

  const fetchNotifications = () => {
    setLoading(true);
    // Get all notifications that aren't deleted
    const notificationStatuses: NotificationStatus[] = [
      "CREATED",
      "ACKNOWLEDGED",
    ];
    const excludeTypes: NotificationType[] = ["FAULT"];

    NotificationService.getNotifications(
      notificationStatuses,
      [],
      excludeTypes,
      setLoading,
      apiKey
    ).subscribe(
      (result: Notification[]) => {
        // Add explicit type assertion to help TypeScript
        const sortedNotifications = [...(result || [])].sort((a, b) => {
          // First sort by status (CREATED comes before ACKNOWLEDGED)
          if (a.status === "CREATED" && b.status !== "CREATED") return -1;
          if (a.status !== "CREATED" && b.status === "CREATED") return 1;

          // Then sort by date (newest first)
          const dateA = a.date_created || 0;
          const dateB = b.date_created || 0;
          return dateB - dateA;
        }) as Notification[]; // Add type assertion

        setNotifications(sortedNotifications);
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching notifications:", error);
        antNotification.error({
          message: "Error",
          description: "Failed to load notifications",
        });
        setLoading(false);
      }
    );
  };

  // Handle new notification from websocket
  const handleNewNotification = () => {
    if (visible) {
      // If drawer is open, refresh the notifications list
      fetchNotifications();
    }
  };

  // Set up websocket listener for notifications
  useWebSocket({
    key: company?.id,
    eventType: "NOTIFICATION",
    onMessage: handleNewNotification,
  });

  const handleOpenNotification = (notif: Notification) => {
    if (!notif.id) return;

    // Immediately navigate to the URL if it exists
    if (notif.url) {
      // Check if it's an internal URL or external URL
      if (
        notif.url.startsWith("/") ||
        notif.url.startsWith(window.location.origin)
      ) {
        // For internal URLs, use navigate
        // If it starts with the origin, remove the origin part
        const path = notif.url.startsWith(window.location.origin)
          ? notif.url.substring(window.location.origin.length)
          : notif.url;

        // Close the drawer before navigating
        onClose();

        // Navigate to the internal path
        navigate(path);
      } else {
        // For external URLs, open in new tab
        window.open(notif.url, "_blank");
      }
    }

    // Only update if notification is unread (CREATED)
    if (notif.status === "CREATED") {
      const updatedNotification = {
        ...notif,
        status: "ACKNOWLEDGED" as NotificationStatus,
      };

      NotificationService.updateNotificationStatus(
        updatedNotification as any,
        null,
        apiKey
      ).subscribe(
        () => {
          // Update the notification in the local state
          const updatedNotifications = notifications.map((n) =>
            n.id === notif.id ? { ...n, status: "ACKNOWLEDGED" } : n
          );

          // Re-sort the notifications after the status update
          const sortedNotifications = [...updatedNotifications].sort((a, b) => {
            if (a.status === "CREATED" && b.status !== "CREATED") return -1;
            if (a.status !== "CREATED" && b.status === "CREATED") return 1;

            const dateA = a.date_created || 0;
            const dateB = b.date_created || 0;
            return dateB - dateA;
          }) as Notification[]; // Add this type assertion

          setNotifications(sortedNotifications);

          // Call the callback to update the count in the header
          if (onNotificationsRead) {
            onNotificationsRead();
          }
        },
        (error: any) => {
          console.error("Error updating notification status:", error);
          antNotification.error({
            message: "Error",
            description: "Failed to update notification status",
          });
        }
      );
    }
  };

  const handleMarkAsRead = (event: React.MouseEvent, notif: Notification) => {
    event.stopPropagation();
    if (!notif.id || notif.status === "ACKNOWLEDGED") return;

    const updatedNotification = {
      ...notif,
      status: "ACKNOWLEDGED" as NotificationStatus,
    };

    NotificationService.updateNotificationStatus(
      updatedNotification as any,
      null,
      apiKey
    ).subscribe(
      () => {
        // Update the notification in the local state and re-sort
        const updatedNotifications = notifications.map((n) =>
          n.id === notif.id ? { ...n, status: "ACKNOWLEDGED" } : n
        );

        // Re-sort the notifications after the status update
        const sortedNotifications = [...updatedNotifications].sort((a, b) => {
          if (a.status === "CREATED" && b.status !== "CREATED") return -1;
          if (a.status !== "CREATED" && b.status === "CREATED") return 1;

          const dateA = a.date_created || 0;
          const dateB = b.date_created || 0;
          return dateB - dateA;
        }) as Notification[]; // Add this type assertion

        setNotifications(sortedNotifications);

        // Call the callback to update the count in the header
        if (onNotificationsRead) {
          onNotificationsRead();
        }
      },
      (error: any) => {
        console.error("Error marking notification as read:", error);
        antNotification.error({
          message: "Error",
          description: "Failed to mark notification as read",
        });
      }
    );
  };

  const handleDeleteNotification = (
    event: React.MouseEvent,
    notif: Notification
  ) => {
    event.stopPropagation();
    if (!notif.id) return;

    const updatedNotification = {
      ...notif,
      status: "DELETED" as NotificationStatus,
    };

    NotificationService.updateNotificationStatus(
      updatedNotification as any,
      null,
      apiKey
    ).subscribe(
      () => {
        // Remove the notification from the local state
        setNotifications(notifications.filter((n) => n.id !== notif.id));
      },
      (error: any) => {
        console.error("Error deleting notification:", error);
        antNotification.error({
          message: "Error",
          description: "Failed to delete notification",
        });
      }
    );
  };

  const handleMarkAllAsRead = () => {
    const unreadNotifications = notifications.filter(
      (notif) => notif.status === "CREATED"
    );

    if (unreadNotifications.length === 0) {
      return;
    }

    setMarkingAllRead(true);

    // Use Promise.all to update all unread notifications
    Promise.all(
      unreadNotifications.map((notif) => {
        const updatedNotification = {
          ...notif,
          status: "ACKNOWLEDGED" as NotificationStatus,
        };

        return new Promise((resolve, reject) => {
          NotificationService.updateNotificationStatus(
            updatedNotification as any,
            null,
            apiKey
          ).subscribe(
            () => resolve(notif.id),
            (error) => reject(error)
          );
        });
      })
    )
      .then(() => {
        // Update all notifications in the local state
        const updatedNotifications = notifications.map((notif) => {
          if (notif.status === "CREATED") {
            return { ...notif, status: "ACKNOWLEDGED" };
          }
          return notif;
        });

        // Re-sort the notifications after the status update
        const sortedNotifications = [...updatedNotifications].sort((a, b) => {
          // After marking all as read, sort by date (newest first)
          const dateA = a.date_created || 0;
          const dateB = b.date_created || 0;
          return dateB - dateA;
        }) as Notification[]; // Add this type assertion

        setNotifications(sortedNotifications);
        setMarkingAllRead(false);

        // Call the callback to update the count in the header
        if (onNotificationsRead) {
          onNotificationsRead();
        }
      })
      .catch((error) => {
        console.error("Error marking all as read:", error);
        antNotification.error({
          message: "Error",
          description: "Failed to mark all notifications as read",
        });
        setMarkingAllRead(false);
        // Refresh to get the current state
        fetchNotifications();
      });
  };

  const handleArchiveAll = () => {
    if (notifications.length === 0) {
      return;
    }

    setArchivingAll(true);

    // Use Promise.all to archive all notifications
    Promise.all(
      notifications.map((notif) => {
        const updatedNotification = {
          ...notif,
          status: "DELETED" as NotificationStatus,
        };

        return new Promise((resolve, reject) => {
          NotificationService.updateNotificationStatus(
            updatedNotification as any,
            null,
            apiKey
          ).subscribe(
            () => resolve(notif.id),
            (error) => reject(error)
          );
        });
      })
    )
      .then(() => {
        // Clear all notifications
        setNotifications([]);
        setArchivingAll(false);
      })
      .catch((error) => {
        console.error("Error archiving all notifications:", error);
        antNotification.error({
          message: "Error",
          description: "Failed to archive all notifications",
        });
        setArchivingAll(false);
        // Refresh to get the current state
        fetchNotifications();
      });
  };

  // New function to archive all read notifications
  const handleArchiveAllRead = () => {
    const readNotifications = notifications.filter(
      (notif) => notif.status === "ACKNOWLEDGED"
    );

    if (readNotifications.length === 0) {
      antNotification.info({
        message: "Info",
        description: "No read notifications to archive",
      });
      return;
    }

    setArchivingAll(true);

    // Use Promise.all to archive all read notifications
    Promise.all(
      readNotifications.map((notif) => {
        const updatedNotification = {
          ...notif,
          status: "DELETED" as NotificationStatus,
        };

        return new Promise((resolve, reject) => {
          NotificationService.updateNotificationStatus(
            updatedNotification as any,
            null,
            apiKey
          ).subscribe(
            () => resolve(notif.id),
            (error) => reject(error)
          );
        });
      })
    )
      .then(() => {
        // Remove archived notifications from the state
        setNotifications(
          notifications.filter((notif) => notif.status !== "ACKNOWLEDGED")
        );
        setArchivingAll(false);
      })
      .catch((error) => {
        console.error("Error archiving read notifications:", error);
        antNotification.error({
          message: "Error",
          description: "Failed to archive read notifications",
        });
        setArchivingAll(false);
        // Refresh to get the current state
        fetchNotifications();
      });
  };

  const getUnreadCount = () => {
    return notifications.filter((n) => n.status === "CREATED").length;
  };

  // Function to format the timestamp correctly
  const formatTimestamp = (timestamp: number | null | undefined): string => {
    if (!timestamp) {
      return "Unknown time";
    }

    // Check if timestamp is too small (missing digits)
    // Unix epoch in milliseconds should be ~13 digits for current dates
    // If it's much smaller, it's likely in seconds instead of milliseconds
    const currentTimeMs = Date.now();
    const expectedDigits = Math.floor(Math.log10(currentTimeMs)) + 1;
    const actualDigits = Math.floor(Math.log10(timestamp)) + 1;

    // If timestamp is in seconds, convert to milliseconds
    if (actualDigits < expectedDigits - 2) {
      timestamp = timestamp * 1000;
    }

    // Use moment to format the date
    return moment(timestamp).fromNow();
  };

  // Function to get read count
  const getReadCount = () => {
    return notifications.filter((n) => n.status === "ACKNOWLEDGED").length;
  };

  // Updated menu items for the dropdown
  const menuItems = [
    {
      key: "markAllRead",
      icon: <CheckSquareOutlined />,
      label: "Mark all as read",
      onClick: handleMarkAllAsRead,
      disabled: getUnreadCount() === 0 || markingAllRead,
    },
    {
      key: "divider",
      type: "divider" as const,
    },
    {
      key: "archiveAll",
      icon: <DeleteOutlined />,
      label: "Archive all",
      onClick: handleArchiveAll,
      disabled: notifications.length === 0 || archivingAll,
    },
    {
      key: "archiveAllRead",
      icon: <DeleteOutlined />,
      label: "Archive all read",
      onClick: handleArchiveAllRead,
      disabled: getReadCount() === 0 || archivingAll,
    },
  ];

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center" }}>
          <BellOutlined style={{ marginRight: 8 }} />
          <span>Notifications</span>
          {getUnreadCount() > 0 && (
            <Badge
              count={getUnreadCount()}
              style={{ marginLeft: 12, backgroundColor: "#1890ff" }}
            />
          )}
        </div>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={400}
      bodyStyle={{ padding: 0 }}
      extra={
        <Space>
          {/* Menu dropdown */}
          <Dropdown
            menu={{ items: menuItems }}
            placement="bottomRight"
            disabled={notifications.length === 0}>
            <Button
              icon={<MoreOutlined />}
              shape="circle"
              type="text"
              disabled={notifications.length === 0}
            />
          </Dropdown>
        </Space>
      }
      footer={
        notifications.length > 0 && getUnreadCount() > 0 ? (
          <div
            style={{
              textAlign: "right",
              padding: "8px 16px",
            }}>
            <Button
              size="small"
              icon={<CheckOutlined />}
              onClick={handleMarkAllAsRead}
              loading={markingAllRead}>
              Mark all as read
            </Button>
          </div>
        ) : null
      }>
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "400px",
          }}>
          <Spin size="large" />
        </div>
      ) : (
        <List
          itemLayout="horizontal"
          dataSource={notifications}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No notifications"
                style={{ margin: "40px 0" }}
              />
            ),
          }}
          renderItem={(item) => (
            <List.Item
              style={{
                cursor: "pointer",
                padding: "16px 24px",
                background: item.status === "CREATED" ? "#f0f7ff" : "white",
                borderBottom: "1px solid #f0f0f0",
                transition: "background-color 0.3s ease",
              }}
              onClick={() => handleOpenNotification(item)}
              onMouseEnter={() => setHoveredNotificationId(item.id || null)}
              onMouseLeave={() => setHoveredNotificationId(null)}
              actions={
                hoveredNotificationId === item.id
                  ? [
                      item.status === "CREATED" && (
                        <Tooltip title="Mark as read">
                          <Button
                            type="text"
                            icon={<CheckOutlined />}
                            onClick={(e) => handleMarkAsRead(e, item)}
                          />
                        </Tooltip>
                      ),
                      <Tooltip title="Archive notification">
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={(e) => handleDeleteNotification(e, item)}
                        />
                      </Tooltip>,
                    ]
                  : []
              }>
              <List.Item.Meta
                title={
                  <Typography.Text strong={item.status === "CREATED"}>
                    {item.title || "Notification"}
                  </Typography.Text>
                }
                description={
                  <div>
                    <Typography.Text
                      style={{
                        display: "block",
                        fontWeight: item.status === "CREATED" ? 500 : 400,
                      }}>
                      {item.content || "No details provided"}
                    </Typography.Text>
                    <Typography.Text
                      type="secondary"
                      style={{ fontSize: "12px" }}>
                      {formatTimestamp(item.date_created)}
                    </Typography.Text>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      )}
    </Drawer>
  );
};

export default AlertTray;
